<script setup>
// Vue twin of <x-quantity-input> (resources/views/components/quantity-input.blade.php +
// App\View\Components\QuantityInput). Same markup and class hooks, no styling of its own:
// components/quantity-input.scss styles both.
//
// Steps the value itself rather than through app.js's delegated handler, so it carries
// neither data-quantity-input nor data-quantity-step. With them, that handler would step the
// field as well and every press would move it twice - the same reason FormCard.vue drops
// data-form-card.
import { computed, useId } from 'vue';

const props = defineProps({
    modelValue: {
        type: Number,
        required: true,
    },
    // Required, as on the Blade side: a bare box of digits does not say what it counts.
    label: {
        type: String,
        required: true,
    },
    // Keeps the label for screen readers but off the screen, where something beside the
    // field already names it - the gift panel's "Quantity" card title. Vue-only: the Blade
    // component has no caller that needs it.
    hideLabel: {
        type: Boolean,
        default: false,
    },
    min: {
        type: Number,
        default: 1,
    },
    // Null for no ceiling.
    max: {
        type: Number,
        default: null,
    },
    id: {
        type: String,
        default: null,
    },
});

const emit = defineEmits(['update:modelValue']);

// useId() only works at setup, so it is taken here rather than inside the computed.
const generatedId = `quantity_input_${useId()}`;
const fieldId = computed(() => props.id ?? generatedId);

// Lowercased so it reads "Decrease quantity", as the Blade class builds it.
const decreaseLabel = computed(() => `Decrease ${props.label.toLowerCase()}`);
const increaseLabel = computed(() => `Increase ${props.label.toLowerCase()}`);

function clamp(value) {
    const floored = Math.max(props.min, value);

    return props.max === null ? floored : Math.min(props.max, floored);
}

function step(by) {
    const next = clamp(props.modelValue + by);

    // At the end of the range nothing changed, so nothing is announced - the same rule
    // app.js follows for the Blade component.
    if (next !== props.modelValue) {
        emit('update:modelValue', next);
    }
}

// A cleared or half-typed field is left alone while the customer is typing; it is put back
// to the last good value when they leave it (onChange below).
function onInput(event) {
    const value = event.target.valueAsNumber;

    if (Number.isInteger(value)) {
        emit('update:modelValue', clamp(value));
    }
}

function onChange(event) {
    event.target.value = props.modelValue;
}
</script>

<template>
    <div class="quantity_field">
        <label :for="fieldId" :class="{ visually_hidden: hideLabel }">{{ label }}</label>

        <div class="quantity_input">
            <button
                type="button"
                class="plain quantity_step"
                :aria-label="decreaseLabel"
                @click="step(-1)"
            >&minus;</button>

            <input
                :id="fieldId"
                type="number"
                :value="modelValue"
                :min="min"
                :max="max ?? undefined"
                step="1"
                inputmode="numeric"
                @input="onInput"
                @change="onChange"
            />

            <button
                type="button"
                class="plain quantity_step"
                :aria-label="increaseLabel"
                @click="step(1)"
            >+</button>
        </div>
    </div>
</template>
